import {
	Button,
	CircularProgress,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { useAtomValue } from "jotai";
import { type FC, useEffect, useMemo, useRef } from "react";
import { useRecentTimelineInfiniteQuery } from "web_myplann/Api/NotificationCenter/useRecentTimelineInfiniteQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { timelineFilterAtom } from "web_myplann/NotificationCenter/Atoms/timelineFilterAtom";
import { EmptyTimelineState } from "web_myplann/NotificationCenter/Components/EmptyTimelineState";
import { MarkAllReadButton } from "web_myplann/NotificationCenter/Components/MarkAllReadButton";
import { RecentGroupSection } from "web_myplann/NotificationCenter/Components/RecentGroupSection";
import { useGroupRecentItems } from "web_myplann/NotificationCenter/Hooks/useGroupRecentItems";
import { useMarkDisplayedAsRead } from "web_myplann/NotificationCenter/Hooks/useMarkDisplayedAsRead";
import { useNavigateFromTimelineItem } from "web_myplann/NotificationCenter/Hooks/useNavigateFromTimelineItem";
import { NOTIFICATION_CENTER_ORIGIN } from "web_myplann/NotificationCenter/Utils/notificationOrigin";
import { EmptyListSkeleton } from "web_myplann/Shared/Components/EmptyListSkeleton";

const SENTINEL_ROOT_MARGIN = "200px";

export const RecentTab: FC = () => {
	const theme = useTheme();
	const { t } = useResource();
	const filter = useAtomValue(timelineFilterAtom);
	const query = useRecentTimelineInfiniteQuery({ type: filter.recentType });
	const navigateToItem = useNavigateFromTimelineItem({
		origin: NOTIFICATION_CENTER_ORIGIN,
	});
	const sentinelRef = useRef<HTMLDivElement>(null);

	const items = useMemo(
		() => query.data?.pages.flatMap((page) => page.items) ?? [],
		[query.data],
	);
	const groups = useGroupRecentItems(items, t);
	const displayedItems = useMemo(
		() => groups.flatMap((group) => group.items),
		[groups],
	);
	const { hasUnread, markAll, isPending } =
		useMarkDisplayedAsRead(displayedItems);

	const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

	useEffect(() => {
		const sentinel = sentinelRef.current;
		if (!sentinel) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
					void fetchNextPage();
				}
			},
			{ threshold: 0, rootMargin: SENTINEL_ROOT_MARGIN },
		);

		observer.observe(sentinel);

		return () => {
			observer.disconnect();
		};
	}, [fetchNextPage, hasNextPage, isFetchingNextPage]);

	if (query.isLoading && !query.data) {
		return (
			<EmptyListSkeleton
				label={t(Resources.NotificationCenter.Title)}
				description={t(Resources.NotificationCenter.Tabs.Recent)}
			/>
		);
	}

	if (query.isError) {
		const handleRetry = () => {
			void query.refetch();
		};
		return (
			<Stack
				height="60vh"
				justifyContent="center"
				alignItems="center"
				gap={1.6}
				padding="0 32px"
				data-testid="notification-center-recent-error"
			>
				<Typography
					fontSize={14}
					fontWeight={500}
					color={theme.palette.grey[700]}
					textAlign="center"
				>
					{t(Resources.NotificationCenter.Common.LoadingError)}
				</Typography>
				<Button variant="outlined" onClick={handleRetry}>
					{t(Resources.NotificationCenter.Common.Retry)}
				</Button>
			</Stack>
		);
	}

	if (groups.length === 0) {
		return <EmptyTimelineState variant="recent" />;
	}

	return (
		<Stack gap={0} data-testid="notification-center-recent-content">
			{hasUnread ? (
				<MarkAllReadButton onClick={markAll} disabled={isPending} />
			) : null}
			{groups.map((group) => (
				<RecentGroupSection
					key={group.key}
					group={group}
					onItemClick={navigateToItem}
				/>
			))}
			<Stack
				ref={sentinelRef}
				data-testid="notification-center-recent-sentinel"
				height={1}
			/>
			{isFetchingNextPage ? (
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="center"
					gap={1}
					py={1.6}
					data-testid="notification-center-recent-loading-more"
				>
					<CircularProgress size={20} />
					<Typography fontSize={12} color={theme.palette.grey[600]}>
						{t(Resources.NotificationCenter.Common.LoadingMore)}
					</Typography>
				</Stack>
			) : null}
		</Stack>
	);
};
